import { Col, Modal, Row, Select } from "antd";
import { Formik } from "formik";
import moment from "moment";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import * as Yup from "yup";
import ButtonStandard from "../../utils/button";
import InputCurrency from "../../utils/inputCurrency";
import TagStatus from "../../utils/tagStatus";
import { Container } from "./styles";

// Services
import { indexAllDropdownBankAccountFarm } from "../../../services/bankAccountFarmService";
import { indexAllDropdownFinancialPaymentMethod } from "../../../services/financialPaymentMethodService";
import { getParameterItems } from "../../../services/generalParameterService";
import { numberMask } from "../../../services/helpersMethodsService";

const CODES_PARAMETERS = [4004, 4006];

const INITIAL_FORM_STATE = {
  financialTransactionMainId: null,
  financialTransactionInstallmentId: null,
  fees: null,
  discount: null,
  financialPaymentMethodId: null,
  bankAccountFarmId: null,
  originValue: null,
  originValueCurrency: null,
  installmentNumber: null,
  installmentTotal: null,
};

function EditInstallmentFinancialTransactionModal({
  visible,
  record,
  onSave,
  onCancel,
}) {
  const formikRef = useRef();

  const [form, setForm] = useState(INITIAL_FORM_STATE);
  const [parametersValue, setParametersValue] = useState([]);
  const [paymentMethodOptions, setPaymentMethodOptions] = useState([]);
  const [isLoadingPaymentMethod, setIsLoadingPaymentMethod] = useState(false);
  const [bankAccountFarmOptions, setBankAccountFarmOptions] = useState([]);
  const [isLoadingBankAccountFarm, setIsLoadingBankAccountFarm] =
    useState(false);

  const {
    translation,
    groupSelected: { id: groupId },
    farmSelected: { id: farmId },
  } = useSelector((state) => state.app);

  const isEnablePaymentMethod = useMemo(() => {
    return (
      parametersValue.find((parameter) => parameter.code === 4004)?.value ===
      "true"
    );
  }, [parametersValue]);

  const isEnableBankAccountFarm = useMemo(() => {
    return (
      parametersValue.find((parameter) => parameter.code === 4006)?.value ===
      "true"
    );
  }, [parametersValue]);

  const validationSchema = useMemo(() => {
    return Yup.object().shape({
      fees: Yup.number().notRequired().nullable(),
      discount: Yup.number().notRequired().nullable(),
      financialPaymentMethodId: Yup.string().notRequired().nullable(),
      bankAccountFarmId:
        isEnableBankAccountFarm === true
          ? Yup.string().required()
          : Yup.string().notRequired().nullable(),
    });
  }, [isEnableBankAccountFarm]);

  const resetForm = useCallback(() => {
    setForm(INITIAL_FORM_STATE);
    formikRef.current.resetForm(INITIAL_FORM_STATE);
  }, []);

  const handleCancel = useCallback(() => {
    resetForm();
    onCancel();
  }, [onCancel, resetForm]);

  const handleSubmit = useCallback(
    (values) => {
      onSave(values);
    },
    [onSave],
  );

  useEffect(() => {
    async function getParameters() {
      let dataResult = [];
      try {
        for (let i = 0; i < CODES_PARAMETERS.length; i++) {
          const code = CODES_PARAMETERS[i];
          const {
            data: { results },
          } = await getParameterItems({ groupId, farmId, code });
          dataResult.push(results);
        }
        setParametersValue(dataResult);
      } catch (error) {}
    }
    if (visible) getParameters();
  }, [visible, groupId, farmId]);

  useEffect(() => {
    async function fetchPaymentMethodOptions() {
      if (isEnablePaymentMethod && groupId && farmId) {
        setIsLoadingPaymentMethod(true);
        try {
          const {
            data: { results },
          } = await indexAllDropdownFinancialPaymentMethod({
            groupId,
            farmId,
          });
          setPaymentMethodOptions(results || []);
        } catch (error) {
          setPaymentMethodOptions([]);
        } finally {
          setIsLoadingPaymentMethod(false);
        }
      }
    }
    fetchPaymentMethodOptions();
  }, [isEnablePaymentMethod, groupId, farmId]);

  useEffect(() => {
    async function fetchBankAccountFarmOptions() {
      if (isEnableBankAccountFarm && groupId && farmId) {
        setIsLoadingBankAccountFarm(true);
        try {
          const {
            data: { results },
          } = await indexAllDropdownBankAccountFarm({ groupId, farmId });
          setBankAccountFarmOptions(results || []);
        } catch (error) {
          setBankAccountFarmOptions([]);
        } finally {
          setIsLoadingBankAccountFarm(false);
        }
      }
    }
    fetchBankAccountFarmOptions();
  }, [isEnableBankAccountFarm, groupId, farmId]);

  useEffect(() => {
    if (visible) {
      setForm({
        financialTransactionMainId: record?.financialTransactionMainId ?? null,
        financialTransactionInstallmentId: record?.id ?? null,
        originValue: record?.value ?? null,
        originValueCurrency: record?.valueCurrency ?? null,
        installmentNumber: record?.installmentNumber ?? null,
        installmentTotal: record?.installmentTotal ?? null,
        fees: record?.fees ?? null,
        discount: record?.discount ?? null,
        financialPaymentMethodId: record?.financialPaymentMethodId ?? null,
        bankAccountFarmId: record?.bankAccountFarmId ?? null,
      });
    }
    return () => {
      setForm(INITIAL_FORM_STATE);
    };
  }, [visible, record]);

  return (
    <Modal
      visible={visible}
      width={650}
      title={translation.financial.transactions.modalEditInstallment.title}
      centered
      closable={true}
      onCancel={handleCancel}
      footer={null}
    >
      <Formik
        ref={formikRef}
        initialValues={form}
        validationSchema={validationSchema}
        enableReinitialize
        onSubmit={handleSubmit}
        render={({
          values,
          errors,
          submitCount,
          setFieldValue,
          handleSubmit,
        }) => (
          <form onSubmit={handleSubmit}>
            <Container>
              {record && (
                <Row type="flex" gutter={16} className="infoBlock">
                  <Col span={4}>
                    <span className="infoLabel">
                      {
                        translation.financial.transactions.table.columns
                          .isSettled
                      }
                    </span>
                    {record.isSettled === false ? (
                      <TagStatus
                        background="#E4F4FF"
                        borderColor="#4A85AE"
                        color="#4A85AE"
                      >
                        {translation.financial.table.tagOpen}
                      </TagStatus>
                    ) : (
                      <TagStatus
                        background="#E1FFE4"
                        borderColor="#106518"
                        color="#106518"
                      >
                        {translation.financial.table.tagSettled}
                      </TagStatus>
                    )}
                  </Col>
                  <Col span={5}>
                    <span className="infoLabel">
                      {
                        translation.financial.transactions.table.columns
                          .documentNumber
                      }
                    </span>
                    <span className="infoValue">
                      {record.documentNumber || "-"}
                    </span>
                  </Col>
                  <Col span={7}>
                    <span className="infoLabel">
                      {
                        translation.financial.transactions.table.columns
                          .entityName
                      }
                    </span>
                    <span className="infoValue">
                      {record.entityName || "-"}
                    </span>
                  </Col>
                  <Col span={4}>
                    <span className="infoLabel">
                      {
                        translation.financial.transactions.table.columns
                          .installment
                      }
                    </span>
                    <span className="infoValue">{`${record.installmentNumber}/${record.installmentTotal}`}</span>
                  </Col>
                  <Col span={4}>
                    <span className="infoLabel">
                      {translation.financial.transactions.table.columns.value}
                    </span>
                    <span className="infoValue">
                      {numberMask(record.value || 0, true)}
                    </span>
                  </Col>
                  <Col span={5}>
                    <span className="infoLabel">
                      {
                        translation.financial.transactions.table.columns
                          .billingDate
                      }
                    </span>
                    <span className="infoValue">
                      {record.billingDate
                        ? moment(record.billingDate).format("DD/MM/YYYY")
                        : "-"}
                    </span>
                  </Col>
                  <Col span={5}>
                    <span className="infoLabel">
                      {
                        translation.financial.transactions.table.columns
                          .expirationDate
                      }
                    </span>
                    <span className="infoValue">
                      {record.expirationDate
                        ? moment(record.expirationDate).format("DD/MM/YYYY")
                        : "-"}
                    </span>
                  </Col>
                </Row>
              )}
              {Object.entries(errors).length > 0 && submitCount > 0 && (
                <Row type="flex" justify="center" align="middle">
                  <label className="error">{translation.error.formError}</label>
                </Row>
              )}
              <Row type="flex" gutter={16}>
                <Col span={12}>
                  <Row>
                    <label>
                      {translation.financial.transactions.form.fees}
                    </label>
                  </Row>
                  <Row>
                    <InputCurrency
                      name="fees"
                      value={values.fees != null ? values.fees : ""}
                      setValue={(floatValue) => {
                        setFieldValue("fees", floatValue);
                      }}
                    />
                  </Row>
                </Col>
                <Col span={12}>
                  <Row>
                    <label>
                      {translation.financial.transactions.form.discount}
                    </label>
                  </Row>
                  <Row>
                    <InputCurrency
                      name="discount"
                      value={values.discount != null ? values.discount : ""}
                      setValue={(floatValue) => {
                        setFieldValue("discount", floatValue);
                      }}
                    />
                  </Row>
                </Col>
              </Row>
              {(isEnablePaymentMethod || isEnableBankAccountFarm) && (
                <Row type="flex" gutter={16} style={{ marginTop: 8 }}>
                  {isEnablePaymentMethod && (
                    <Col span={12}>
                      <Row>
                        <label>
                          {
                            translation.financial.transactions.form
                              .financialPaymentMethodId
                          }
                        </label>
                      </Row>
                      <Row>
                        <Select
                          name="financialPaymentMethodId"
                          allowClear
                          loading={isLoadingPaymentMethod}
                          value={
                            paymentMethodOptions != null &&
                            values.financialPaymentMethodId != null
                              ? paymentMethodOptions.find(
                                  (payMethod) =>
                                    payMethod.id ===
                                    values.financialPaymentMethodId,
                                )
                                ? values.financialPaymentMethodId
                                : undefined
                              : undefined
                          }
                          showSearch
                          optionFilterProp="children"
                          filterOption={(input, option) =>
                            option.props.children
                              .toLowerCase()
                              .indexOf(input.toLowerCase()) >= 0
                          }
                          placeholder={translation.defaultSelectPlaceholder}
                          onChange={(value) => {
                            setFieldValue("financialPaymentMethodId", value);
                          }}
                        >
                          {paymentMethodOptions
                            .filter(
                              (payMethod) => payMethod.status === "Active",
                            )
                            .map((financialPaymentMethod) => (
                              <Select.Option
                                key={financialPaymentMethod.id}
                                value={financialPaymentMethod.id}
                              >
                                {financialPaymentMethod.name}
                              </Select.Option>
                            ))}
                        </Select>
                      </Row>
                    </Col>
                  )}
                  {isEnableBankAccountFarm && (
                    <Col span={12}>
                      <Row>
                        <label
                          className={
                            errors.bankAccountFarmId && submitCount > 0
                              ? "error"
                              : ""
                          }
                        >
                          {
                            translation.financial.transactions.form
                              .bankAccountFarmId
                          }
                          *
                        </label>
                      </Row>
                      <Row>
                        <Select
                          name="bankAccountFarmId"
                          value={values.bankAccountFarmId || undefined}
                          placeholder={translation.defaultSelectPlaceholder}
                          loading={isLoadingBankAccountFarm}
                          showSearch
                          optionFilterProp="children"
                          filterOption={(input, option) =>
                            option.props.children
                              .toLowerCase()
                              .indexOf(input.toLowerCase()) >= 0
                          }
                          onChange={(value) =>
                            setFieldValue("bankAccountFarmId", value)
                          }
                        >
                          {bankAccountFarmOptions
                            .filter((baf) => baf.status === "Active")
                            .map((bankAccountFarm) => (
                              <Select.Option
                                key={bankAccountFarm.id}
                                value={bankAccountFarm.id}
                              >
                                {bankAccountFarm.description}
                              </Select.Option>
                            ))}
                        </Select>
                      </Row>
                    </Col>
                  )}
                </Row>
              )}
              {/* Footer */}
              <Row
                type="flex"
                justify="center"
                align="middle"
                gutter={16}
                style={{ marginTop: 16 }}
              >
                <Col>
                  <ButtonStandard
                    type="button"
                    buttonType="type4"
                    size="s"
                    onClick={handleCancel}
                  >
                    {translation.buttons.cancel}
                  </ButtonStandard>
                </Col>
                <Col>
                  <ButtonStandard type="submit" buttonType="type1" size="s">
                    {translation.buttons.confirm}
                  </ButtonStandard>
                </Col>
              </Row>
            </Container>
          </form>
        )}
      />
    </Modal>
  );
}

export default EditInstallmentFinancialTransactionModal;
